import React, { useEffect, useState } from 'react';
import {
  AppBar,
  Badge,
  Box,
  Button,
  Chip,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Menu as MenuIcon,
  EmojiEvents as LeagueIcon,
  HowToReg as RegisterIcon,
  CalendarMonth as AvailabilityIcon,
  AdminPanelSettings as AdminIcon,
  Dashboard as DashboardIcon,
  People as MembersIcon,
  LocationOn as VenuesIcon,
  Event as EventIcon,
  Celebration as SocialEventIcon,
  Assessment as ReportsIcon,
  Groups as MyTeamIcon,
  ArrowDropDown as ArrowDropDownIcon,
  Description as MatchReportIcon,
} from '@mui/icons-material';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useCanAccessAdmin, usePermissions } from '../../contexts/PermissionsContext';
import unifiedApi, { buildApiUrl } from '../../utils/unifiedApi';
import { DEFAULT_BRAND_LOGO, DEFAULT_BRAND_NAME, useOrganizationBranding } from '../../contexts/OrganizationBrandingContext';
import useInstallPrompt from '../../hooks/useInstallPrompt';
import LoginButton from '../auth/LoginButton';
import UserMenu from './UserMenu';
import MobileDrawer from './MobileDrawer';
import InstallPromptSheet from './InstallPromptSheet';
import OutstandingBalanceBadge from '../balance/OutstandingBalanceBadge';
import ChatNavButton from '../chat/ChatNavButton';

const GlobalNav = ({
  currentUserTenantId,
  availabilityLoading,
  onAvailabilityClick,
}) => {
  const location = useLocation();
  const { isAuthenticated, user } = useAuth();
  const canAccessAdmin = useCanAccessAdmin();
  const { canManageAnyTeam, canViewSocialEvents } = usePermissions();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [myTeams, setMyTeams] = useState([]);
  const [teamMenuAnchor, setTeamMenuAnchor] = useState(null);
  const [pendingReportsCount, setPendingReportsCount] = useState(0);
  const [conflictCount, setConflictCount] = useState(0);
  const { name: orgName, logoUrl: orgLogoUrl, loaded: brandingLoaded } = useOrganizationBranding();
  const [failedLogoUrl, setFailedLogoUrl] = useState(null);
  const brandName = orgName || DEFAULT_BRAND_NAME;
  const brandLogo = orgLogoUrl && failedLogoUrl !== orgLogoUrl ? buildApiUrl(orgLogoUrl) : DEFAULT_BRAND_LOGO;
  const logoHeight = isMobile ? 32 : 40;
  const {
    isVisible: showInstallPrompt,
    platform: installPromptPlatform,
    dismissPrompt,
    triggerAndroidInstall,
  } = useInstallPrompt({ enabled: isAuthenticated && isMobile });

  useEffect(() => {
    if (!isAuthenticated) {
      setMyTeams([]);
      return;
    }
    let cancelled = false;
    const fetchTeams = async () => {
      try {
        const teams = await unifiedApi.getMyTeams();
        if (!cancelled) setMyTeams(teams || []);
      } catch {
        if (!cancelled) setMyTeams([]);
      }
    };
    fetchTeams();
    return () => { cancelled = true; };
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated || !canManageAnyTeam()) {
      setPendingReportsCount(0);
      return;
    }
    let cancelled = false;
    const fetchPending = async () => {
      try {
        const reports = await unifiedApi.getPendingMatchReports();
        if (!cancelled) setPendingReportsCount(reports?.length || 0);
      } catch {
        if (!cancelled) setPendingReportsCount(0);
      }
    };
    fetchPending();
    return () => { cancelled = true; };
  }, [isAuthenticated, canManageAnyTeam, location.pathname]);

  useEffect(() => {
    if (!isAuthenticated || !canAccessAdmin) {
      setConflictCount(0);
      return;
    }
    let cancelled = false;
    const fetchConflicts = async () => {
      try {
        const result = await unifiedApi.getMatchReportConflictCount();
        if (!cancelled) setConflictCount(result?.totalConflicts || 0);
      } catch {
        if (!cancelled) setConflictCount(0);
      }
    };
    fetchConflicts();
    return () => { cancelled = true; };
  }, [isAuthenticated, canAccessAdmin, location.pathname]);

  const isAdminMode = location.pathname.startsWith('/admin');
  const adminOrgId = location.pathname.match(/\/admin\/organization\/(\d+)/)?.[1];
  const pathOrgId = location.pathname.match(/\/organization\/(\d+)/)?.[1];
  const effectiveOrgId = pathOrgId || adminOrgId;
  const mobileMenuBadgeCount = isAuthenticated && canManageAnyTeam() ? pendingReportsCount : 0;

  const isNavActive = (navType) => {
    const pathname = location.pathname;

    // Admin nav highlighting
    if (navType === 'admin-dashboard') return pathname === '/admin';
    if (navType === 'admin-members') return pathname.includes('/admin') && pathname.includes('/members');
    if (navType === 'admin-merge-users') return pathname === '/admin/merge-users';
    if (navType === 'admin-leagues') return pathname.includes('/admin') && pathname.includes('/leagues');
    if (navType === 'admin-venues') return pathname === '/admin/venues';
    if (navType === 'admin-events') return pathname.startsWith('/admin/events');
    if (navType === 'admin-social-events') return pathname.startsWith('/admin/social-events');
    if (navType === 'admin-reports') return pathname.startsWith('/admin/reports');

    // Global nav highlighting
    const myTeamSlugs = myTeams.map((t) => t.teamSlug);
    const teamSlugFromPath = pathname.startsWith('/teams/') ? pathname.split('/')[2] : null;
    const isOnMyTeamPage = teamSlugFromPath && myTeamSlugs.includes(teamSlugFromPath);

    if (navType === 'leagues') {
      return (
        pathname === '/' ||
        pathname === '/home' ||
        pathname === '/leagues' ||
        (pathname.startsWith('/leagues/') && !pathname.includes('/register')) ||
        pathname === '/teams' ||
        (pathname.startsWith('/teams/') && !isOnMyTeamPage)
      );
    }
    if (navType === 'register') return pathname === '/register' || pathname.includes('/register');
    if (navType === 'my-team') return isOnMyTeamPage;
    if (navType === 'match-reports') return pathname.startsWith('/match-reports');
    return false;
  };

  // Button style helper
  const navButtonSx = (active) => ({
    textTransform: 'none',
    fontSize: '0.875rem',
    color: 'inherit',
    backgroundColor: active ? 'rgba(255,255,255,0.2)' : 'transparent',
    fontWeight: active ? 600 : 400,
    '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' },
  });

  // Determine desktop nav items
  const renderDesktopNav = () => {
    if (isAdminMode && canAccessAdmin) {
      return (
        <Stack direction="row" spacing={1} alignItems="center">
          <Button
            component={Link}
            to="/admin"
            startIcon={<DashboardIcon />}
            sx={navButtonSx(isNavActive('admin-dashboard'))}
          >
            Dashboard
          </Button>
          <Button
            component={Link}
            to="/admin/members"
            startIcon={<MembersIcon />}
            sx={navButtonSx(isNavActive('admin-members'))}
          >
            Members
          </Button>
          <Badge badgeContent={conflictCount} color="error" max={99}>
            <Button
              component={Link}
              to="/admin/leagues"
              startIcon={<LeagueIcon />}
              sx={navButtonSx(isNavActive('admin-leagues'))}
            >
              Leagues
            </Button>
          </Badge>
          <Button
            component={Link}
            to="/admin/venues"
            startIcon={<VenuesIcon />}
            sx={navButtonSx(isNavActive('admin-venues'))}
          >
            Venues
          </Button>
          <Button
            component={Link}
            to="/admin/events"
            startIcon={<EventIcon />}
            sx={navButtonSx(isNavActive('admin-events'))}
          >
            Events
          </Button>
          {canViewSocialEvents() && (
            <Button
              component={Link}
              to="/admin/social-events"
              startIcon={<SocialEventIcon />}
              sx={navButtonSx(isNavActive('admin-social-events'))}
            >
              Socials
            </Button>
          )}
          <Button
            component={Link}
            to="/admin/reports"
            startIcon={<ReportsIcon />}
            sx={navButtonSx(isNavActive('admin-reports'))}
          >
            Reports
          </Button>
        </Stack>
      );
    }

    return (
      <Stack direction="row" spacing={1} alignItems="center">
        <Button
          component={Link}
          to="/"
          startIcon={<LeagueIcon />}
          sx={navButtonSx(isNavActive('leagues'))}
        >
          Leagues
        </Button>
        <Button
          component={Link}
          to="/register"
          startIcon={<RegisterIcon />}
          sx={navButtonSx(isNavActive('register'))}
        >
          Register
        </Button>
        {isAuthenticated && myTeams.length === 1 && (
          <Button
            component={Link}
            to={`/teams/${myTeams[0].teamSlug}`}
            startIcon={<MyTeamIcon />}
            sx={navButtonSx(isNavActive('my-team'))}
          >
            My Team
          </Button>
        )}
        {isAuthenticated && myTeams.length > 1 && (
          <>
            <Button
              onClick={(e) => setTeamMenuAnchor(e.currentTarget)}
              startIcon={<MyTeamIcon />}
              endIcon={<ArrowDropDownIcon />}
              sx={navButtonSx(isNavActive('my-team'))}
              aria-haspopup="true"
            >
              My Team
            </Button>
            <Menu
              anchorEl={teamMenuAnchor}
              open={Boolean(teamMenuAnchor)}
              onClose={() => setTeamMenuAnchor(null)}
            >
              {myTeams.map((team) => (
                <MenuItem
                  key={team.teamSlug}
                  onClick={() => {
                    setTeamMenuAnchor(null);
                    navigate(`/teams/${team.teamSlug}`);
                  }}
                >
                  <ListItemText
                    primary={team.teamName}
                    secondary={team.leagueName}
                  />
                </MenuItem>
              ))}
            </Menu>
          </>
        )}
        {isAuthenticated && (
          <Button
            onClick={onAvailabilityClick}
            disabled={availabilityLoading}
            startIcon={<AvailabilityIcon />}
            sx={navButtonSx(false)}
          >
            My Availability
          </Button>
        )}
        {isAuthenticated && canManageAnyTeam() && (
          <Button
            component={Link}
            to="/match-reports/pending"
            startIcon={
              <Badge badgeContent={pendingReportsCount} color="error" max={99}>
                <MatchReportIcon />
              </Badge>
            }
            sx={navButtonSx(isNavActive('match-reports'))}
          >
            Match Reports
          </Button>
        )}
      </Stack>
    );
  };

  return (
    <>
      <AppBar position="static" sx={{ backgroundColor: '#2E4156' }}>
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          {/* Left: Logo + title + admin chip */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, minWidth: 0 }}>
            {brandingLoaded ? (
              <img
                src={brandLogo}
                alt={brandName}
                onError={() => setFailedLogoUrl(orgLogoUrl)}
                // Wide logos can shrink down to a square on narrow phones so the toolbar controls still fit.
                style={{ height: logoHeight, width: 'auto', maxWidth: isMobile ? 96 : 160, minWidth: logoHeight, objectFit: 'contain', marginRight: 8, flexShrink: 1 }}
              />
            ) : (
              // Keep the space while branding loads so the QuailGame defaults don't flash first.
              <Box data-testid="brand-logo-placeholder" sx={{ width: logoHeight, height: logoHeight, mr: 1, flexShrink: 0 }} />
            )}
            <Typography
              variant={isMobile ? 'h6' : 'h5'}
              component={Link}
              to="/"
              noWrap
              aria-label={brandingLoaded ? undefined : 'Home'}
              sx={{
                textDecoration: 'none',
                color: 'inherit',
                fontSize: { xs: '1.1rem', sm: '1.25rem' },
                minWidth: 0,
                maxWidth: { xs: '40vw', sm: 320 },
              }}
            >
              {brandingLoaded ? brandName : ''}
            </Typography>
            {isAdminMode && canAccessAdmin && (
              <Chip
                icon={<AdminIcon />}
                label="ADMIN"
                size="small"
                clickable={false}
                onClick={() => {}}
                sx={{
                  backgroundColor: 'rgba(255,152,0,0.9)',
                  color: 'white',
                  fontWeight: 'bold',
                  '& .MuiChip-icon': { color: 'white' },
                }}
              />
            )}
          </Box>

          {/* Right side */}
          {!isMobile ? (
            <Stack direction="row" spacing={2} alignItems="center">
              {renderDesktopNav()}

              {isAuthenticated && <ChatNavButton />}

              {isAuthenticated && <OutstandingBalanceBadge />}

              {isAuthenticated ? (
                <UserMenu
                  user={user}
                  currentUserTenantId={currentUserTenantId}
                  isAdminMode={isAdminMode}
                  canAccessAdmin={canAccessAdmin}
                  effectiveOrgId={effectiveOrgId}
                />
              ) : (
                <LoginButton
                  variant="text"
                  color="inherit"
                  sx={{
                    textTransform: 'none',
                    fontWeight: 500,
                    ml: 2,
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' },
                  }}
                >
                  Sign In
                </LoginButton>
              )}
            </Stack>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {isAuthenticated && <ChatNavButton />}
              {isAuthenticated && <OutstandingBalanceBadge />}
              {isAuthenticated && (
                <UserMenu
                  user={user}
                  currentUserTenantId={currentUserTenantId}
                  isAdminMode={isAdminMode}
                  canAccessAdmin={canAccessAdmin}
                  effectiveOrgId={effectiveOrgId}
                />
              )}
              <IconButton
                color="inherit"
                onClick={() => setDrawerOpen(true)}
                edge="end"
                aria-label="Open navigation menu"
                aria-haspopup="true"
                aria-controls="global-navigation-drawer"
                aria-expanded={drawerOpen}
              >
                <Badge badgeContent={mobileMenuBadgeCount} color="error" max={99}>
                  <MenuIcon />
                </Badge>
              </IconButton>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      <MobileDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        isAuthenticated={isAuthenticated}
        user={user}
        currentUserTenantId={currentUserTenantId}
        isAdminMode={isAdminMode}
        canAccessAdmin={canAccessAdmin}
        effectiveOrgId={effectiveOrgId}
        availabilityLoading={availabilityLoading}
        onAvailabilityClick={onAvailabilityClick}
        isNavActive={isNavActive}
        myTeams={myTeams}
        canManageAnyTeam={canManageAnyTeam()}
        canViewSocialEvents={canViewSocialEvents()}
        pendingReportsCount={pendingReportsCount}
        conflictCount={conflictCount}
      />

      <InstallPromptSheet
        open={showInstallPrompt}
        platform={installPromptPlatform}
        onNotNow={dismissPrompt}
        onInstall={triggerAndroidInstall}
      />
    </>
  );
};

export default GlobalNav;
